import {useCallback, useEffect, useRef, useState, type KeyboardEvent} from 'react'
import {CHAT_FONT_SCALE_OPTIONS, chatFontScaleLabel} from './chatFontScale'

export interface ChatFontSizeControlProps {
  scale: number
  onChange: (scale: number) => void
}

const lastIndex = CHAT_FONT_SCALE_OPTIONS.length - 1

/**
 * The chat header's text-size menu, in the export menu's styling. Follows the ARIA menu pattern:
 * opening focuses the current size, arrows/Home/End move between sizes (one tab stop), and
 * Escape — wherever focus is — or a choice closes it and hands focus back to the button.
 */
export const ChatFontSizeControl = ({scale, onChange}: ChatFontSizeControlProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false)
    if (restoreFocus) toggleRef.current?.focus()
  }, [])

  const toggle = () => {
    if (open) {
      close(false)
      return
    }
    const current = CHAT_FONT_SCALE_OPTIONS.findIndex((o) => o.scale === scale)
    setFocusIndex(current >= 0 ? current : 0)
    setOpen(true)
  }

  useEffect(() => {
    if (open) itemRefs.current[focusIndex]?.focus()
  }, [open, focusIndex])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) close(false)
    }
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open, close])

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next: Record<string, number> = {
      ArrowDown: focusIndex === lastIndex ? 0 : focusIndex + 1,
      ArrowUp: focusIndex === 0 ? lastIndex : focusIndex - 1,
      Home: 0,
      End: lastIndex,
    }
    if (e.key in next) {
      e.preventDefault()
      setFocusIndex(next[e.key])
    }
  }

  const choose = (next: number) => {
    close(true)
    onChange(next)
  }

  return (
    <div className='ai-chat-v2-sidebar__dropdown' ref={dropdownRef}>
      <button
        ref={toggleRef}
        type='button'
        className={`ai-chat-v2-sidebar__icon-btn ${open ? 'active' : ''}`}
        data-testid='omics-ai-chat-font-size-btn'
        onClick={toggle}
        title={`Text size: ${chatFontScaleLabel(scale)}`}
        aria-label='Chat text size'
        aria-haspopup='menu'
        aria-expanded={open}
      >
        <i className='fas fa-text-height' />
      </button>
      {open && (
        <div
          className='ai-chat-v2-sidebar__dropdown-menu'
          role='menu'
          aria-label='Chat text size'
          onKeyDown={onMenuKeyDown}
        >
          {CHAT_FONT_SCALE_OPTIONS.map((option, index) => (
            <button
              key={option.scale}
              ref={(el) => {
                itemRefs.current[index] = el
              }}
              type='button'
              role='menuitemradio'
              aria-checked={option.scale === scale}
              tabIndex={index === focusIndex ? 0 : -1}
              className='ai-chat-v2-sidebar__dropdown-item'
              data-testid={`omics-ai-chat-font-size-${option.scale}`}
              onClick={() => choose(option.scale)}
            >
              <i className={option.scale === scale ? 'fas fa-check' : 'fas fa-fw'} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
